import React, { useEffect, useRef } from 'react';
import { AUTH_BASE } from '../../services/oidcService';
import { initials, avatarColor, pastelAvatarColor } from '../chatHelpers';
import { useAuthStore } from '../../store/authStore';
import { useAvatarStore } from '../../store/avatarStore';
import { ensureAvatar, refreshAvatar } from '../../services/avatarCache';
import AvatarShell from './AvatarShell';

/**
 * User avatar served by the IdP (`{AUTH_BASE}/avatars/{id}` — the uploaded
 * picture, linked GitHub avatar, or generated pixel art, in that order).
 * Falls back to the legacy initials-on-color circle when there is no user id
 * or the image fails to load (e.g. offline). Children (unread badge,
 * presence dot) overlay the circle like they did on the old avatar div.
 *
 * On the desktop the bytes come from `services/avatarCache.js`: a cached
 * `blob:` URL published into `avatarStore.srcByUserId` wins over the network
 * URL; outside Tauri nothing is published and the network `<img>` is used.
 *
 * `tone="pastel"` (board cards and list rows) draws the initials fallback in
 * the person's pastel with dark, bold initials — the Boards Redesign look;
 * the picture itself is unaffected.
 */
export default function UserAvatar({ userId, name, size, tone, className = '', style = {}, children }) {
  const selfId = useAuthStore((s) => s.user?.id);
  const version = useAvatarStore((s) => s.version);
  const cached = useAvatarStore((s) =>
    userId != null ? s.srcByUserId[String(userId)] : undefined
  );
  const isSelf = String(userId) === String(selfId);
  // The version this instance has already refreshed for: a bump refreshes the
  // mounted self avatars once; a self avatar that mounts later gets the
  // already-refreshed cache instead of forcing another GET.
  const refreshedVersionRef = useRef(version);

  useEffect(() => {
    if (userId != null) ensureAvatar(userId);
  }, [userId]);

  useEffect(() => {
    if (isSelf && version && userId != null && version !== refreshedVersionRef.current) {
      refreshedVersionRef.current = version;
      refreshAvatar(userId);
    }
  }, [isSelf, userId, version]);

  const bust = isSelf && version ? `?v=${version}` : '';
  const src = cached ?? (userId != null ? `${AUTH_BASE}/avatars/${userId}${bust}` : null);

  const pastel = tone === 'pastel';
  const sizeStyle = {
    ...(size != null ? { width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.4)) } : {}),
    ...(pastel ? { color: '#0f0f12', fontWeight: 700 } : {}),
  };

  return (
    <AvatarShell
      src={src}
      bg={pastel ? pastelAvatarColor(userId ?? name) : avatarColor(userId ?? name)}
      fallback={initials(name)}
      sizeStyle={sizeStyle}
      className={className}
      style={style}
    >
      {children}
    </AvatarShell>
  );
}
